/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Registrations Service - Super Admin Registrations API
 * ============================================================================
 *
 * Service layer for registration request management API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams } from '@/services/api/types';
import { Registration, RegistrationFilterParams, RegistrationActionRequest } from '@/types/superadmin.types';

const BASE_URL = '/superadmin/registrations';

/**
 * Registrations service for Super Admin
 */
export const registrationsService = {
  /**
   * Get paginated list of registration requests
   */
  async getRegistrations(
    params?: PaginationParams & RegistrationFilterParams
  ): Promise<PaginatedResponse<Registration>> {
    const response = await get<ApiResponse<PaginatedResponse<Registration>>>(BASE_URL, params);
    return response.data;
  },

  /**
   * Get registration by ID
   */
  async getRegistration(id: string): Promise<Registration> {
    const response = await get<ApiResponse<Registration>>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Approve a registration request
   */
  async approveRegistration(id: string, data?: RegistrationActionRequest): Promise<Registration> {
    const response = await post<ApiResponse<Registration>>(`${BASE_URL}/${id}/approve`, data);
    return response.data;
  },

  /**
   * Reject a registration request
   */
  async rejectRegistration(id: string, data?: RegistrationActionRequest): Promise<Registration> {
    const response = await post<ApiResponse<Registration>>(`${BASE_URL}/${id}/reject`, data);
    return response.data;
  },

  /**
   * Get registration statistics
   */
  async getRegistrationStats(): Promise<{
    pendingCount: number;
    approvedThisMonth: number;
    rejectedThisMonth: number;
    totalProcessed: number;
  }> {
    const response = await get<ApiResponse<{
      pendingCount: number;
      approvedThisMonth: number;
      rejectedThisMonth: number;
      totalProcessed: number;
    }>>(`${BASE_URL}/stats`);
    return response.data;
  },
};

export default registrationsService;
